import React, { useState, useEffect } from "react";
import {
  CalendarDays,
  MapPin,
  MessageCircle,
  IndianRupee,
  UserSquare,
  Phone,
  
} from "lucide-react";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import { useNavigate } from "react-router-dom";


const CompletedCard = () => {
  const [openProjectId, setOpenProjectId] = useState(null);
  const { projects, fetchProjects, isLoading, error } = useProjectStatus();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects("completed");
  }, [fetchProjects]);

  if (error) {
    return <p className="text-center text-red-500 text-sm">{error}</p>;
  }

  if (isLoading) {
    return <p className="text-center text-gray-500 text-sm">Loading projects...</p>;
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 text-sm">No completed projects found.</p>
      </div>
    );
  }


  // Update the getGridClass function to match CompleteRequestCard
  const getGridClass = () => {
    if (openProjectId) return "grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 sm:gap-16";
    
    const baseClasses = "grid grid-cols-1 gap-3 sm:gap-4 justify-center mx-auto";
    const maxWidth = "max-w-5xl"; // Consistent max width for all cases
    
    if (projects.length <= 4) {
      let cols;
      if (projects.length === 1) {
        cols = "sm:grid-cols-1 md:grid-cols-1 w-full sm:w-[300px]"; // Responsive width for single project
      } else if (projects.length === 2) {
        cols = "sm:grid-cols-2 md:grid-cols-2 w-full sm:w-[600px]"; // Responsive width for two projects
      } else if (projects.length === 3) {
        cols = "sm:grid-cols-2 md:grid-cols-3"; // More responsive breakpoints
      } else {
        cols = "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"; // Progressive grid columns
      }
      return `${baseClasses} ${cols} ${maxWidth}`;
    }
    
    return `${baseClasses} sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 max-w-7xl`;
  };

  return (
    <div className="container mx-auto px-2 sm:px-4 lg:px-6">
      <div className={getGridClass()}>
        {projects.sort((a, b) => {
          if (a.id === openProjectId) return -1;
          if (b.id === openProjectId) return 1;
          return 0;
        }).map((project, idx) => (
          <div
            key={project.id || idx}
            className={`bg-white rounded-xl shadow-md p-2 sm:p-3 md:p-4 relative w-full mx-auto transition-all duration-300 flex flex-col ${
              openProjectId === project.id ? 'h-auto' : 'h-[270px] sm:h-[290px] md:h-[300px]'
            }`}
          >
            <div className="text-center mb-4">
              <h2 className="text-sm sm:text-base md:text-lg font-bold mb-1 truncate">
                {project.builder_company || "Unknown Builder"}
              </h2>
              <div className="w-full h-px bg-black"></div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-2 sm:gap-0">
              <div className="flex items-center gap-1">
                <UserSquare className="w-4 h-4 text-gray-600" />
                <span className="text-gray-600 text-xs sm:text-sm">
                  {project.builder_ducktail_id || "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="w-4 h-4 text-gray-600" />
                <span className="text-gray-600 text-xs sm:text-sm">
                  {project.builder_contact || "N/A"}
                </span>
              </div>
            </div>

            <div className="space-y-4 flex-grow">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-xs sm:text-xs">Project Starting Date</div>
                  <div className="text-gray-600 text-xs sm:text-xs">
                    {project.starting_date
                      ? new Date(project.starting_date).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-xs sm:text-xs">Project Completion Date</div>
                  <div className="text-gray-600 text-xs sm:text-xs">
                    {project.completed_date
                      ? new Date(project.completed_date).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-xs sm:text-xs">Project Location</div>
                  <div className="text-gray-600 text-xs sm:text-xs truncate">
                    {project.project_location || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Update the document button */}
            <div className="flex justify-center gap-3 mt-3">
              <button
                type="button"
                className="flex-1 max-w-[90px] py-1 px-1.5 border border-gray-300 rounded-lg text-gray-800 text-[10px] sm:text-xs font-medium flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all"
              >
                <MessageCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5" />
                <span>Chat</span>
              </button>
              <button
                type="button"
                className="flex-1 max-w-[90px] py-1 px-1.5 border border-gray-300 rounded-lg text-gray-800 text-[10px] sm:text-xs font-medium flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all"
              >
                <IndianRupee className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5" />
                <span>Payment</span>
              </button>
              <button
                onClick={() => setOpenProjectId(openProjectId === project.id ? null : project.id)}
                className="flex-1 max-w-[90px] py-1 px-1.5 border border-gray-300 rounded-lg text-gray-800 text-[10px] sm:text-xs font-medium flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>Docs</span>
              </button>
            </div>

            {/* Update the document panel */}
            {openProjectId === project.id && (
              <div 
                className="mt-4 bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300"
                style={{
                  animation: "slideDown 0.3s ease-out forwards"
                }}
              >
                <h3 className="text-sm sm:text-base font-bold border-b pb-2 px-4 pt-4">DOCUMENTS</h3>
                <div className="p-4">
                  <div className="overflow-x-auto">
                    {[
                      "Specification Report",
                      "Warranty Documents",
                      "Completion Certificate",
                      "Site Image",
                    ].map((doc, index) => (
                      <div
                        key={index}
                        className={`flex justify-between py-2 px-3 text-xs sm:text-sm ${
                          index % 2 === 0 ? "bg-gray-100" : "bg-white"
                        }`}
                      >
                        <span className="w-1/12 font-medium">
                          {["I", "II", "III", "IV"][index]}
                        </span>
                        <span className="w-1/2">{doc}</span>
                        <div className="flex gap-2">
                          <a href=" " className="text-blue-500 hover:text-blue-600">
                            View
                          </a>
                          <a href=" " className="text-blue-500 hover:text-blue-600">
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Update animation keyframes */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
            <button
        className="fixed bottom-3 right-3 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
        onClick={() => navigate("/customer/help")}
      >
        <img src="/assets/Support.png" alt="Icon" className="w-7 h-7 sm:w-9 sm:h-9" />
      </button>
    </div>
  );
};

export default CompletedCard;
